"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar, User } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16">
        <div className="h-12 w-3/4 animate-pulse rounded-md bg-muted/30" />
        <div className="mt-8 h-96 w-full animate-pulse rounded-lg bg-muted/30" />
        <div className="mt-8 space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/30" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/30" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <p className="text-muted-foreground">Failed to load post. Please try again later.</p>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center">
        <p className="text-muted-foreground">Post not found.</p>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl px-4 py-16 md:py-24">
      {}
      <h1 className="font-display text-4xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl">
        {post.title}
      </h1>

      {}
      <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        {post.author && (
          <div className="flex items-center gap-2">
            <User className="size-4" />
            <span>{post.author.name}</span>
          </div>
        )}
        {post.date && (
          <div className="flex items-center gap-2">
            <Calendar className="size-4" />
            <time dateTime={post.date}>
              {new Date(post.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </time>
          </div>
        )}
      </div>

      <Separator className="my-8" />

      {}
      {post.featuredImage && (
        <div className="my-12 overflow-hidden rounded-lg shadow-medium">
          <img
            src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/chevrolet_suburban_executive.png"
            alt="Chevy Suburban 2025"
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg prose-img:shadow-medium"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      <Separator className="my-12" />

      {}
      {post.author && (
        <div className="glass-effect mt-12 flex items-start gap-4 rounded-lg p-6">
          <Avatar className="size-16">
            {post.author.avatar?.["96"] && (
              <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549622/img/professional_chauffeur_avatar.png" alt={post.author.name} />
            )}
            <AvatarFallback className="bg-primary text-foreground-primary text-lg font-semibold">
              {post.author.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="font-display text-lg font-semibold">About {post.author.name}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Author at PHOENIX ENTERPRISE TCP
            </p>
          </div>
        </div>
      )}

      {}
      {((post.categories && post.categories.length > 0) || (post.tags && post.tags.length > 0)) && (
        <div className="mt-12 flex flex-wrap gap-6">
          {post.categories && post.categories.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Categories
              </h4>
              <div className="flex flex-wrap gap-2">
                {post.categories.map((category) => (
                  <span
                    key={category.id}
                    className="rounded-md border border-border bg-accent px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent-hover"
                  >
                    {category.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {post.tags && post.tags.length > 0 && (
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="rounded-md border border-border bg-accent px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent-hover"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section id="single-post-template-section" className="section-padding bg-background">
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}